'use client'

import { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Clock, Timer, Play, Square, Hourglass } from 'lucide-react'
import { useTranslations } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import { formatHours, formatTimeFromSeconds } from '@/lib/time-utils'
import Link from 'next/link'

interface IssueTimeTrackerProps {
  issueId: number
  issueSubject: string
  projectId?: number
  totalHours?: number
  variant?: 'compact' | 'full'
  className?: string
  onOpenTimeTracking?: () => void
}

export function IssueTimeTracker({
  issueId,
  issueSubject,
  projectId,
  totalHours,
  variant = 'compact',
  className = '',
  onOpenTimeTracking
}: IssueTimeTrackerProps) {
  const activeTimer = useTimeTracking((state) => state.activeTimer)
  const getElapsedTime = useTimeTracking((state) => state.getElapsedTime)
  const startTimer = useTimeTracking((state) => state.startTimer)
  const stopTimer = useTimeTracking((state) => state.stopTimer)
  const pauseTimer = useTimeTracking((state) => state.pauseTimer)
  const resumeTimer = useTimeTracking((state) => state.resumeTimer)

  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const isTimerActive = activeTimer?.issueId === issueId
  const t = useTranslations('issuesPage')

  useEffect(() => {
    if (!isTimerActive) {
      setElapsedSeconds(0)
      return
    }

    // Calculate elapsed time directly instead of using getElapsedTime in dependencies
    const calculateElapsed = () => {
      if (!activeTimer) return 0
      if (activeTimer.isPaused) {
        return activeTimer.pausedTime
      }
      const currentElapsed = Math.floor((Date.now() - activeTimer.startTime) / 1000)
      return activeTimer.pausedTime + currentElapsed
    }

    // Update elapsed time immediately
    setElapsedSeconds(calculateElapsed())

    // Only update timer if not paused
    if (activeTimer && !activeTimer.isPaused) {
      const interval = setInterval(() => {
        setElapsedSeconds(calculateElapsed())
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [isTimerActive, activeTimer])

  const handleStartTimer = () => {
    // Only start if there's no active timer
    if (activeTimer) {
      return
    }
    
    startTimer({
      issueId,
      issueSubject,
      projectId: projectId || 0,
      activityId: 8, // Development
      activityName: 'Development',
      description: `Working on #${issueId}`
    })
    
  
  }

  const handleStopTimer = () => {
    // Use current elapsedSeconds state instead of calling getElapsedTime
    const elapsed = isTimerActive ? elapsedSeconds : 0

    // Always dispatch event with elapsed seconds
    const eventDetail = {
      issueId,
      elapsedSeconds: elapsed,
      projectId
    }

    window.dispatchEvent(new CustomEvent('openTimeTracking', {
      detail: eventDetail
    }))

    // Also call the callback if provided
    if (onOpenTimeTracking) {
      onOpenTimeTracking()
    }
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {totalHours !== undefined && totalHours > 0 && (
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {formatHours(totalHours)} {t('timeEntries')}
            </Badge>
        )}

        {isTimerActive ? (
            <Badge
            variant="default"
            className="text-xs bg-red-500 hover:bg-red-600 cursor-pointer animate-pulse"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleStopTimer()
            }}
          >
              <Timer className="h-3 w-3 mr-1" />
              {formatTimeFromSeconds(elapsedSeconds)} • {t('stopTimer')}
          </Badge>
        ) : null}
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Logged Time */}
      {totalHours !== undefined && totalHours > 0 && (
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('totalLoggedTime')}</div>
              <div className="text-2xl font-bold mt-0.5">{formatHours(totalHours)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Active Timer */}
      {isTimerActive ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <div className="inline-block" style={{
                  animation: 'hourglass-flip 2s ease-in-out infinite'
                }}>
                  <Hourglass className="h-5 w-5 text-red-500" />
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider mb-1 text-red-600">{t('timerActive')}</div>
                <div className="text-2xl font-mono font-bold tabular-nums">
                  {formatTimeFromSeconds(elapsedSeconds)}
                </div>
              </div>
            </div>
          </div>
          <Button
            size="lg"
            variant="destructive"
            onClick={handleStopTimer}
            className="w-full font-semibold bg-red-500 hover:bg-red-600 text-white"
          >
            <Square className="h-4 w-4 fill-current mr-2" />
            {t('stopAndLog')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Button
            size="lg"
            variant="default"
            onClick={handleStartTimer}
            className="w-full font-semibold"
          >
            <Play className="h-4 w-4 mr-2" />
            {t('startTracking')}
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => onOpenTimeTracking?.()}
            className="w-full font-semibold"
          >
            <Clock className="h-4 w-4 mr-2" />
            {t('logTime')}
          </Button>
        </div>
      )}
    </div>
  )
}
